import { listEstablishmentsQueryOptions } from "@packages/api-client/hooks/establishmentController";
import { listHerdsByOrganizationQueryOptions } from "@packages/api-client/hooks/livestockController";
import { Action } from "@packages/shared-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	invalidateOnboardingSnapshot,
	isOnboardingEligible,
	type OnboardingStepId,
	onboardingSnapshotQueryOptions,
	useUpdateOnboardingPreferences,
} from "@/api/onboarding";
import { useOnboardingEvents } from "@/api/onboardingEvents";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useEstablishment } from "@/hooks/useEstablishment";
import { rememberSetupPageEscape } from "@/lib/onboarding/setupPageEscape";
import { capabilityGroupOf } from "./-components/home/onboarding/onboardingPresentation";
import {
	resolveGuideTone,
	resolveGuideView,
} from "./-components/home/onboarding/SetupGuide";
import { SetupAnimalImport } from "./-components/setup/SetupAnimalImport";
import { SetupFarmForm } from "./-components/setup/SetupFarmForm";
import { SetupFarmSaved } from "./-components/setup/SetupFarmSaved";
import { SetupHelpIntro } from "./-components/setup/SetupHelpIntro";
import { SetupOpening } from "./-components/setup/SetupOpening";
import { SetupPageShell } from "./-components/setup/SetupPageShell";
import { SetupStepper } from "./-components/setup/SetupStepper";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/onboarding")({
	validateSearch: (search: Record<string, unknown>): { import?: string } => ({
		import:
			typeof search.import === "string" && search.import
				? search.import
				: undefined,
	}),
	loaderDeps: ({ search }) => ({ farm: search.farm }),
	loader: ({ context, params, deps }) => {
		// Both paint above the fold — the snapshot decides which step is drawn and
		// the farm list decides whether the goal step has to ask "Em qual fazenda?".
		context.queryClient.ensureQueryData(
			onboardingSnapshotQueryOptions(params.orgId),
		);
		context.queryClient.ensureQueryData(
			listEstablishmentsQueryOptions(params.orgId),
		);
		if (deps.farm) {
			context.queryClient.ensureQueryData(
				listHerdsByOrganizationQueryOptions(params.orgId, {
					establishmentId: deps.farm,
				}),
			);
		}
	},
	component: SetupPage,
});

/** First step that is not finished — the one the page is currently asking about. */
function currentStepId(
	steps: { id: OnboardingStepId; status: string }[],
): OnboardingStepId | null {
	return steps.find((step) => step.status !== "DONE")?.id ?? null;
}

/**
 * The setup page: a dedicated full-page surface that takes a brand-new
 * organization from "account created" to an oriented first view of Home.
 *
 * It is a page rather than a card on home, and it carries the farm form rather
 * than opening a dialog, because the whole flow is one question at a time with
 * nothing else competing for the screen. It exists only while there is setup
 * work left to do — every view that is not `EXPANDED` hands the user back to
 * home, where the minimized bar, the recap and the completion state live.
 */
function SetupPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const { import: importId } = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		selectedEstablishment,
		selectedEstablishmentId,
		setSelectedEstablishmentId,
		establishments,
	} = useEstablishment();
	const preferences = useUpdateOnboardingPreferences();
	const recordEvent = useOnboardingEvents(orgId);
	const checkPermission = useHasPermission(orgId);
	const canCreateFarm = checkPermission(Action.MANAGE_ESTABLISHMENTS);

	const { data: snapshot, isLoading } = useQuery(
		onboardingSnapshotQueryOptions(orgId),
	);
	const importEstablishmentId =
		selectedEstablishmentId ??
		(establishments.length === 1 ? establishments[0].id : null);
	const { data: herdsData } = useQuery({
		...listHerdsByOrganizationQueryOptions(orgId, {
			establishmentId: importEstablishmentId ?? "",
		}),
		enabled: importEstablishmentId !== null,
	});
	const herds = (herdsData?.data ?? [])
		.filter((herd) => herd.isActive)
		.map((herd) => ({
			id: herd.id,
			name: herd.name,
		}));

	// Step 2 is reached from step 1's own action, so which of the two is drawn is
	// local: both are the same server milestone (`FARM_CREATED`).
	const [formOpen, setFormOpen] = useState(false);
	const [savedFarmName, setSavedFarmName] = useState<string | null>(null);
	const [continuePending, setContinuePending] = useState(false);
	// `Pular esta etapa` on the import surface: the milestone is unchanged, so
	// this is only which of the two step-3 surfaces is drawn. It is local and not
	// a preference — a reload puts the import back, exactly like step 2's form.
	const [importSkipped, setImportSkipped] = useState(false);
	const savedRef = useRef<HTMLParagraphElement>(null);
	const viewedStepRef = useRef<OnboardingStepId | null>(null);

	const view = resolveGuideView(snapshot);
	const tone = resolveGuideTone(snapshot, view);
	const eligible = isOnboardingEligible(snapshot);
	const capabilityGroup = capabilityGroupOf(
		eligible ? snapshot.availableGoals : [],
	);

	// Every milestone is server-derived; the page never promotes a step locally.
	const steps = eligible ? snapshot.steps : [];
	const stepId = currentStepId(steps);

	// Once per organization per session, and only when the page actually
	// rendered — "exposed" must mean seen, not fetched (§11).
	useEffect(() => {
		if (view !== "EXPANDED") return;
		const returning = tone === "RETURNING";
		recordEvent("onboarding_exposed", {
			...(returning ? { stepId: "FIRST_RECORD" as const } : {}),
			properties: {
				capabilityGroup,
				...(returning ? { stepId: "FIRST_RECORD" as const } : {}),
			},
		});
	}, [view, tone, capabilityGroup, recordEvent]);

	// One `step_viewed` per transition, not per render: the ref makes a re-render
	// (or a StrictMode double-invoke) idempotent without a network round-trip.
	useEffect(() => {
		if (view !== "EXPANDED" || !stepId) return;
		if (viewedStepRef.current === stepId) return;
		viewedStepRef.current = stepId;
		recordEvent("onboarding_step_viewed", {
			stepId,
			properties: { stepId },
		});
	}, [view, stepId, recordEvent]);

	// The control that had focus is gone once the farm is saved, so restoring it
	// would drop the user at the top of the document.
	useEffect(() => {
		if (savedFarmName) savedRef.current?.focus();
	}, [savedFarmName]);

	const goHome = () =>
		navigate({
			to: "/app/org/$orgId",
			params: { orgId },
			search: (prev) => prev,
		});

	if (isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-base-200">
				<LoadingSpinner size="lg" />
			</div>
		);
	}

	// Anything but `EXPANDED` belongs on home: the minimized bar, the recap and
	// the one-time completion state all live there, and an ineligible or
	// dismissed guide has no page at all.
	if (view !== "EXPANDED" || !eligible || !stepId) {
		return (
			<Navigate
				to="/app/org/$orgId"
				params={{ orgId }}
				search={(prev) => prev}
				replace
			/>
		);
	}

	/** `Fazer depois` — a real preference. The guide reopens minimized on home. */
	const later = () => {
		preferences.mutate({
			organizationId: orgId,
			data: { guideState: "MINIMIZED" },
		});
		recordEvent("onboarding_minimized");
		goHome();
	};

	/**
	 * `Ir para o início` — leaves the page without changing `guideState`. The
	 * escape is remembered for the tab so home does not send the user straight
	 * back; a new session lands here again, exactly as a first arrival would.
	 */
	const escapeHome = () => {
		rememberSetupPageEscape(orgId);
		goHome();
	};

	const handleFarmSaved = (farmName: string) => {
		setFormOpen(false);
		setSavedFarmName(farmName);
	};

	const continueAfterFarms = async () => {
		setContinuePending(true);
		try {
			// The farm milestone is derived server-side; re-read it only when the
			// user is done adding farms rather than guessing the next step locally.
			await invalidateOnboardingSnapshot(queryClient, orgId);
		} finally {
			setContinuePending(false);
		}
	};

	const onFirstRecord = stepId === "FIRST_RECORD";
	const farmName =
		selectedEstablishment?.name ??
		(establishments.length === 1 ? establishments[0].name : null);
	const showAnimalImport =
		snapshot.availableGoals.includes("LIVESTOCK") && !importSkipped;
	const showHelpIntro = onFirstRecord && !showAnimalImport;

	const startHomeTour = () => {
		preferences.mutate({
			organizationId: orgId,
			data: { guideState: "MINIMIZED" },
		});
		recordEvent("onboarding_minimized");
		rememberSetupPageEscape(orgId);
		navigate({
			to: "/app/org/$orgId",
			params: { orgId },
			search: (prev) => ({ ...prev, tour: "overview" as const }),
		});
	};

	const goToAnimals = () => {
		if (!importEstablishmentId) return;
		navigate({
			to: "/app/org/$orgId/livestock",
			params: { orgId },
			search: { tab: "animals", farm: importEstablishmentId },
		});
	};

	return (
		<SetupPageShell
			farmName={onFirstRecord ? farmName : null}
			onGoHome={escapeHome}
			highlightHelp={showHelpIntro}
			contentClassName={onFirstRecord ? "sm:py-11" : "sm:py-13"}
			columnClassName={
				onFirstRecord
					? showAnimalImport
						? "max-w-[1180px] gap-7"
						: "max-w-[900px] gap-7"
					: formOpen
						? "max-w-[760px] gap-8"
						: "max-w-[760px] gap-10"
			}
		>
			<SetupStepper
				steps={steps}
				currentStepId={stepId}
				readOnly={snapshot.availableGoals.length === 0}
			/>

			{/* Mounted empty so the region is registered before the first
			    announcement. Off-screen on purpose: the stepper already shows
			    `Fazenda salva no RuralOps` in place of step 2's instruction, so a
			    banner saying the same thing would be a second, louder copy of a
			    fact the page has already changed to state. Focus still moves here,
			    because the control that had it is gone. */}
			<output aria-live="polite">
				{savedFarmName && (
					<p
						ref={savedRef}
						tabIndex={-1}
						className="sr-only"
						data-testid="setup-farm-saved"
					>
						{t("setupGuide.farmStep.saved")}
					</p>
				)}
			</output>

			{onFirstRecord && showAnimalImport ? (
				<SetupAnimalImport
					orgId={orgId}
					establishmentId={importEstablishmentId}
					establishments={establishments}
					herds={herds}
					importId={importId}
					onChooseFarm={setSelectedEstablishmentId}
					onImportChange={(mediaFileId) =>
						navigate({
							to: ".",
							search: (prev) => ({
								...prev,
								import: mediaFileId ?? undefined,
							}),
							replace: true,
						})
					}
					onDone={goToAnimals}
					onManualEntry={() => {
						if (!importEstablishmentId) return;
						navigate({
							to: "/app/org/$orgId/livestock/$establishmentId/animals/new",
							params: { orgId, establishmentId: importEstablishmentId },
						});
					}}
					onSkip={() => setImportSkipped(true)}
					onLater={later}
				/>
			) : showHelpIntro ? (
				<SetupHelpIntro onContinue={startHomeTour} />
			) : formOpen ? (
				<SetupFarmForm
					orgId={orgId}
					onSaved={handleFarmSaved}
					onLater={later}
					onPlanLimitContinue={continueAfterFarms}
					continuePending={continuePending}
				/>
			) : savedFarmName ? (
				<SetupFarmSaved
					farmName={savedFarmName}
					onAddAnother={() => {
						setSavedFarmName(null);
						setFormOpen(true);
					}}
					onContinue={continueAfterFarms}
					continuePending={continuePending}
				/>
			) : (
				<SetupOpening
					canCreateFarm={canCreateFarm}
					onStart={() => {
						setFormOpen(true);
						recordEvent("onboarding_step_started", {
							stepId: "FARM_CREATED",
							properties: { stepId: "FARM_CREATED" },
						});
					}}
					onLater={later}
				/>
			)}
		</SetupPageShell>
	);
}
